/**
 * Supplier Declaration Management Page
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Paper,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Home as HomeIcon,
  Business as SupplierIcon,
  Description as DeclarationIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { brand } from '../../../theme';
import DashboardTab from '../components/DashboardTab';
import SuppliersTab from '../components/SuppliersTab';
import DeclarationsTab from '../components/DeclarationsTab';
import EmailLogsTab from '../components/EmailLogsTab';
import AlertsDashboard from '../components/AlertsDashboard';
import ComplianceReportTab from '../components/ComplianceReportTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const SupplierDeclarationPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto' }}>
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/process')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <SupplierIcon sx={{ mr: 0.5 }} fontSize="small" />
            Supplier Declarations
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: brand.textPrimary }}>
            Supplier Declaration Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Automate supplier declaration lifecycle with AI-powered processing and expiry monitoring
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: `1px solid ${brand.borderSubtle}`,
              px: 2,
              '& .MuiTabs-indicator': { backgroundColor: brand.orange },
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
              '& .Mui-selected': { color: `${brand.orange} !important` },
            }}
          >
            <Tab icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />
            <Tab icon={<DeclarationIcon />} label="Declarations" iconPosition="start" />
            <Tab icon={<SupplierIcon />} label="Suppliers" iconPosition="start" />
            <Tab icon={<EmailIcon />} label="Email Logs" iconPosition="start" />
            <Tab icon={<NotificationsIcon />} label="Alerts" iconPosition="start" />
            <Tab icon={<ReportIcon />} label="Compliance Report" iconPosition="start" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <DashboardTab />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <DeclarationsTab />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <SuppliersTab />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <EmailLogsTab />
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <AlertsDashboard />
          </TabPanel>

          <TabPanel value={activeTab} index={5}>
            <ComplianceReportTab />
          </TabPanel>
        </Paper>
      </Container>
    </Box>
  );
};

export default SupplierDeclarationPage;
